import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Eye,
  FlaskConical,
  Loader2,
  Plus,
  Power,
  RefreshCw,
  RotateCw,
  Search,
  Trash2,
  Webhook,
  X,
} from "lucide-react";
import { useHive } from "@/store/useHive";
import { useWebhooks } from "@/store/webhookStore";
import { APPS } from "@/data/apps";
import { Badge } from "@/components/ui/primitives";
import { Modal } from "@/components/ui/Modal";
import { timeAgo } from "@/lib/utils";
import { WebhookCreateModal, WebhookRevealModal } from "./WebhookModals";
import { DELIVERY_TONE, DIRECTION_META, SCOPE_META } from "./webhookUi";
import type { HiveWebhook, WebhookDelivery, WebhookDirection, WebhookScope } from "@/types";

const accent = APPS.integrations.accent;

function HookCard({ hook, selected, onClick }: { hook: HiveWebhook; selected: boolean; onClick: () => void }) {
  const meta = DIRECTION_META[hook.direction];
  const scopeMeta = SCOPE_META[hook.scope];
  const integration = useHive((s) => s.integrations.find((i) => i.id === hook.integrationId));
  const failing = hook.lastStatus === "failed" || (!hook.active && hook.disabledReason === "auto_failures");

  return (
    <motion.button
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className="w-full rounded-2xl border bg-surface/50 p-3.5 text-left transition"
      style={{ borderColor: selected ? accent : "hsl(var(--border))", boxShadow: selected ? `0 10px 28px -16px ${accent}aa` : undefined }}
    >
      <div className="flex items-start gap-2.5">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg" style={{ background: `${meta.color}1f`, color: meta.color }}>
          <meta.icon size={15} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-app">{hook.name}</span>
            {!hook.active && <Badge tone="paused">off</Badge>}
            {failing && <AlertTriangle size={12} className="shrink-0 text-rose-500" />}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-app-2">
            <span>{meta.label}</span>
            <span>·</span>
            <span className="flex items-center gap-1"><scopeMeta.icon size={10} /> {scopeMeta.label}</span>
            {integration && (
              <>
                <span>·</span>
                <span style={{ color: integration.color }}>{integration.name}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="mt-2 truncate font-mono text-[10px] text-app-2">
        {hook.direction === "outbound" ? hook.url : hook.inboundPath}
      </div>
      <div className="mt-2 flex items-center justify-between border-t border-app pt-2 text-[10px] text-app-2">
        <span className="tabular-nums">
          {hook.deliveryCount} deliveries{hook.failureCount > 0 && <span className="text-rose-500"> · {hook.failureCount} failed</span>}
        </span>
        <span>{hook.lastDeliveryAt ? `last ${timeAgo(hook.lastDeliveryAt)} ago` : "no deliveries yet"}</span>
      </div>
    </motion.button>
  );
}

function DeliveryRow({ d, hook }: { d: WebhookDelivery; hook: HiveWebhook }) {
  const [open, setOpen] = useState(false);
  const redeliver = useWebhooks((s) => s.redeliver);
  const tone = DELIVERY_TONE[d.status] ?? "#94a3b8";
  const payloadStr = useMemo(() => {
    try {
      return JSON.stringify(d.payload, null, 2);
    } catch {
      return String(d.payload);
    }
  }, [d.payload]);

  return (
    <div className="rounded-lg border border-app bg-surface-2/30">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 px-2.5 py-2 text-left">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: tone }} />
        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-app-1">{d.event}</span>
        {d.statusCode != null && <span className="text-[10px] tabular-nums text-app-2">HTTP {d.statusCode}</span>}
        {d.durationMs != null && <span className="text-[10px] tabular-nums text-app-2">{d.durationMs}ms</span>}
        {d.attempt > 1 && <span className="text-[10px] text-amber-500">×{d.attempt}</span>}
        <span className="text-[10px] text-app-2">{timeAgo(d.createdAt)}</span>
        {open ? <ChevronDown size={12} className="text-app-2" /> : <ChevronRight size={12} className="text-app-2" />}
      </button>
      {open && (
        <div className="space-y-2 border-t border-app p-2.5">
          {d.error && (
            <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-1.5 text-[11px] text-rose-500">
              {d.error}
            </div>
          )}
          <pre className="max-h-44 overflow-auto rounded-md bg-surface-2/60 p-2 font-mono text-[10px] leading-relaxed text-app-1">
            {payloadStr}
          </pre>
          {d.responseBody && (
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wider text-app-2">Endpoint response</div>
              <pre className="max-h-24 overflow-auto rounded-md bg-surface-2/60 p-2 font-mono text-[10px] text-app-1">{d.responseBody}</pre>
            </div>
          )}
          <div className="flex items-center justify-between text-[10px] text-app-2">
            <span>{d.sourceIp ? `from ${d.sourceIp}` : `delivery ${d.id}`}</span>
            {hook.direction === "outbound" && hook.canManage && (
              <button
                onClick={() => void redeliver(hook.id, d.id)}
                className="flex items-center gap-1 rounded-md border border-app px-2 py-1 font-medium text-app-1 transition hover:border-app-strong"
              >
                <RefreshCw size={10} /> Redeliver
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function HookDetail({ hook, onDelete }: { hook: HiveWebhook; onDelete: () => void }) {
  const update = useWebhooks((s) => s.update);
  const test = useWebhooks((s) => s.test);
  const rotateSecret = useWebhooks((s) => s.rotateSecret);
  const revealUrl = useWebhooks((s) => s.revealUrl);
  const deliveries = useWebhooks((s) => s.deliveries[hook.id]);
  const deliveriesLoading = useWebhooks((s) => s.deliveriesLoading === hook.id);
  const loadDeliveries = useWebhooks((s) => s.loadDeliveries);
  const workspaces = useHive((s) => s.codeWorkspaces);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    void loadDeliveries(hook.id);
  }, [hook.id, loadDeliveries]);

  const meta = DIRECTION_META[hook.direction];
  const scopeMeta = SCOPE_META[hook.scope];
  const workspace = workspaces.find((w) => w.id === hook.workspaceId);
  const autoDisabled = !hook.active && hook.disabledReason === "auto_failures";

  return (
    <motion.div key={hook.id} initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} className="flex h-full flex-col">
      <div className="border-b border-app p-4">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl" style={{ background: `${meta.color}1f`, color: meta.color }}>
            <meta.icon size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold text-app">{hook.name}</div>
            <div className="text-xs text-app-2">
              {meta.label} · {scopeMeta.label}
              {hook.createdByName ? ` · by ${hook.createdByName}` : ""}
            </div>
          </div>
          <Badge tone={hook.active ? (hook.lastStatus === "failed" ? "degraded" : "connected") : "paused"}>
            {hook.active ? (hook.lastStatus === "failed" ? "Failing" : "Active") : "Disabled"}
          </Badge>
        </div>

        {hook.description && <p className="mt-2 text-xs leading-relaxed text-app-2">{hook.description}</p>}

        {/* sticky error surfaces */}
        {autoDisabled && (
          <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-2.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-rose-500">
              <AlertTriangle size={12} /> Auto-disabled after {hook.consecutiveFailures} straight failures
            </div>
            <div className="mt-1 text-[11px] leading-relaxed text-app-2">
              {hook.lastError ?? "The endpoint kept rejecting deliveries."} Fix the endpoint, then re-enable and test.
            </div>
            {hook.canManage && (
              <button
                onClick={() => void update(hook.id, { active: true })}
                className="mt-2 flex items-center gap-1 rounded-md bg-rose-500 px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:brightness-105"
              >
                <Power size={11} /> Re-enable webhook
              </button>
            )}
          </div>
        )}
        {!autoDisabled && hook.lastStatus === "failed" && hook.lastError && (
          <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-amber-500">
              <AlertTriangle size={12} /> Last delivery failed {hook.lastDeliveryAt ? `${timeAgo(hook.lastDeliveryAt)} ago` : ""}
            </div>
            <div className="mt-1 text-[11px] leading-relaxed text-app-2">{hook.lastError}</div>
            <div className="mt-1 text-[10px] text-app-2">
              {hook.consecutiveFailures > 0 && `${hook.consecutiveFailures} consecutive failures — auto-disables at 10. `}
              Retries run automatically (30s, 2m) — or redeliver from the log below.
            </div>
          </div>
        )}

        {/* actions */}
        {hook.canManage && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button
              disabled={testing || !hook.active}
              onClick={() => {
                setTesting(true);
                void test(hook.id).finally(() => setTesting(false));
              }}
              className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105 disabled:opacity-50"
              style={{ background: accent }}
            >
              {testing ? <Loader2 size={11} className="animate-spin" /> : <FlaskConical size={11} />} Send test
            </button>
            {hook.direction === "inbound" && (
              <button
                onClick={() => void revealUrl(hook.id)}
                className="flex items-center gap-1 rounded-md border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
              >
                <Eye size={11} /> Reveal URL
              </button>
            )}
            <button
              onClick={() => void rotateSecret(hook.id)}
              className="flex items-center gap-1 rounded-md border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
            >
              <RotateCw size={11} /> Rotate secret
            </button>
            <button
              onClick={() => void update(hook.id, { active: !hook.active })}
              className="flex items-center gap-1 rounded-md border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
            >
              <Power size={11} /> {hook.active ? "Disable" : "Enable"}
            </button>
            <button
              onClick={onDelete}
              className="flex items-center gap-1 rounded-md border border-app px-2.5 py-1.5 text-[11px] font-medium text-rose-500 transition hover:border-rose-500/40"
            >
              <Trash2 size={11} /> Delete
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4 no-scrollbar">
        {/* config */}
        <div className="rounded-xl border border-app bg-surface-2/30 p-3 text-xs">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-app-2">Configuration</div>
          <div className="space-y-1.5">
            {hook.direction === "outbound" ? (
              <>
                <div className="flex items-start justify-between gap-2">
                  <span className="shrink-0 text-app-2">Endpoint</span>
                  <span className="break-all text-right font-mono text-[11px] text-app-1">{hook.url}</span>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <span className="shrink-0 text-app-2">Events</span>
                  <span className="flex flex-wrap justify-end gap-1">
                    {hook.events.map((e) => (
                      <span key={e} className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-app-1">{e}</span>
                    ))}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex items-start justify-between gap-2">
                <span className="shrink-0 text-app-2">Endpoint</span>
                <span className="break-all text-right font-mono text-[11px] text-app-1">{hook.inboundPath}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-app-2">Signing secret</span>
              <span className="font-mono text-[11px] text-app-1">whsec_…{hook.secretHint}</span>
            </div>
            {workspace && (
              <div className="flex items-center justify-between">
                <span className="text-app-2">Workspace</span>
                <span className="text-app-1">{workspace.name}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-app-2">Created</span>
              <span className="text-app-1">{timeAgo(hook.createdAt)} ago</span>
            </div>
          </div>
        </div>

        {/* deliveries */}
        <div>
          <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-app-2">
            <span>Recent deliveries</span>
            <button onClick={() => void loadDeliveries(hook.id)} className="flex items-center gap-1 text-app-2 transition hover:text-app">
              <RefreshCw size={11} className={deliveriesLoading ? "animate-spin" : ""} /> refresh
            </button>
          </div>
          {!deliveries || deliveries.length === 0 ? (
            <div className="rounded-lg border border-dashed border-app p-3 text-center text-[11px] leading-relaxed text-app-2">
              {deliveriesLoading
                ? "Loading…"
                : hook.direction === "inbound"
                  ? "Nothing received yet. Reveal the URL and POST an event — it lands here in real time."
                  : "No deliveries yet. Send a test event, or wait for a subscribed event to fire."}
            </div>
          ) : (
            <div className="space-y-1.5">
              {deliveries.map((d) => (
                <DeliveryRow key={d.id} d={d} hook={hook} />
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export function WebhooksPanel({
  integrationFilter,
  onClearIntegrationFilter,
  onOpenGuide,
}: {
  integrationFilter: string | null;
  onClearIntegrationFilter: () => void;
  onOpenGuide: () => void;
}) {
  const webhooks = useWebhooks((s) => s.webhooks);
  const status = useWebhooks((s) => s.status);
  const hydrate = useWebhooks((s) => s.hydrate);
  const loadEvents = useWebhooks((s) => s.loadEvents);
  const selectedId = useWebhooks((s) => s.selectedId);
  const select = useWebhooks((s) => s.select);
  const remove = useWebhooks((s) => s.remove);
  const integrations = useHive((s) => s.integrations);

  const [direction, setDirection] = useState<"all" | WebhookDirection>("all");
  const [scope, setScope] = useState<"all" | WebhookScope>("all");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<HiveWebhook | null>(null);

  useEffect(() => {
    void hydrate();
    void loadEvents();
  }, [hydrate, loadEvents]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return webhooks.filter(
      (w) =>
        (direction === "all" || w.direction === direction) &&
        (scope === "all" || w.scope === scope) &&
        (!integrationFilter || w.integrationId === integrationFilter) &&
        (!needle ||
          w.name.toLowerCase().includes(needle) ||
          (w.url ?? "").toLowerCase().includes(needle) ||
          w.events.some((e) => e.includes(needle))),
    );
  }, [webhooks, direction, scope, q, integrationFilter]);

  const selected = webhooks.find((w) => w.id === selectedId);
  const filterIntegration = integrations.find((i) => i.id === integrationFilter);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-app px-4 py-2">
        <div className="flex items-center gap-0.5 rounded-lg border border-app bg-surface-2/50 p-0.5">
          {(["all", "inbound", "outbound"] as const).map((d) => (
            <button
              key={d}
              onClick={() => setDirection(d)}
              className="rounded-md px-2.5 py-1 text-[11px] font-medium capitalize transition"
              style={{ background: direction === d ? accent : "transparent", color: direction === d ? "#000" : "var(--text-2)" }}
            >
              {d}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-0.5 rounded-lg border border-app bg-surface-2/50 p-0.5">
          {(["all", "user", "workspace", "org"] as const).map((sc) => (
            <button
              key={sc}
              onClick={() => setScope(sc)}
              className="rounded-md px-2.5 py-1 text-[11px] font-medium transition"
              style={{ background: scope === sc ? `${accent}22` : "transparent", color: scope === sc ? accent : "var(--text-2)" }}
            >
              {sc === "all" ? "All levels" : SCOPE_META[sc].label.split(" ")[0]}
            </button>
          ))}
        </div>
        <div className="flex min-w-[160px] flex-1 items-center gap-1.5 rounded-lg border border-app bg-surface px-2.5 py-1.5">
          <Search size={12} className="shrink-0 text-app-2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, URL, event…"
            className="w-full bg-transparent text-xs text-app outline-none placeholder:text-app-2"
          />
        </div>
        {filterIntegration && (
          <button
            onClick={onClearIntegrationFilter}
            className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-medium"
            style={{ background: `${filterIntegration.color}1f`, color: filterIntegration.color }}
          >
            {filterIntegration.name} <X size={11} />
          </button>
        )}
        <button
          onClick={onOpenGuide}
          className="flex items-center gap-1.5 rounded-lg border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
        >
          <BookOpen size={12} /> Guide
        </button>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-black transition hover:brightness-105"
          style={{ background: accent }}
        >
          <Plus size={13} /> New webhook
        </button>
      </div>

      {/* body */}
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto p-4 no-scrollbar">
          {status === "loading" && webhooks.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-app-2">
              <Loader2 size={15} className="animate-spin" /> Loading webhooks…
            </div>
          ) : filtered.length === 0 ? (
            <div className="mx-auto max-w-md py-14 text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-surface-2 text-app-2">
                <Webhook size={22} />
              </div>
              <div className="mt-3 text-sm font-semibold text-app">
                {webhooks.length === 0 ? "No webhooks yet" : "Nothing matches the filters"}
              </div>
              <p className="mt-1 text-xs leading-relaxed text-app-2">
                {webhooks.length === 0
                  ? "Webhooks are how most integrations talk to Hive: inbound endpoints receive provider events (GitHub pushes, Stripe payments…), outbound webhooks push signed Hive events to your own services."
                  : "Loosen the direction/level filters or clear the search."}
              </p>
              {webhooks.length === 0 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setCreateOpen(true)}
                    className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold text-black transition hover:brightness-105"
                    style={{ background: accent }}
                  >
                    <Plus size={13} /> Create your first webhook
                  </button>
                  <button
                    onClick={onOpenGuide}
                    className="flex items-center gap-1.5 rounded-lg border border-app px-3.5 py-2 text-xs font-medium text-app-1 transition hover:border-app-strong"
                  >
                    <BookOpen size={13} /> Read the guide
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <AnimatePresence>
                {filtered.map((w) => (
                  <HookCard key={w.id} hook={w} selected={selectedId === w.id} onClick={() => select(w.id)} />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        <div className="hidden w-[400px] shrink-0 border-l border-app lg:block">
          <AnimatePresence mode="wait">
            {selected ? (
              <HookDetail hook={selected} onDelete={() => setConfirmDelete(selected)} />
            ) : (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid h-full place-items-center p-8 text-center">
                <div>
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-surface-2 text-app-2">
                    <Webhook size={22} />
                  </div>
                  <p className="mt-3 text-sm text-app-2">
                    Select a webhook to inspect its deliveries, rotate the secret, or send a test event.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <WebhookCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        presetIntegrationId={integrationFilter}
        onOpenGuide={onOpenGuide}
      />
      <WebhookRevealModal />

      {confirmDelete && (
        <Modal
          open
          onClose={() => setConfirmDelete(null)}
          accent="#f43f5e"
          icon={<Trash2 size={18} />}
          title="Delete webhook?"
          subtitle={confirmDelete.name}
          width={440}
        >
          <p className="text-xs leading-relaxed text-app-2">
            {confirmDelete.direction === "inbound"
              ? "The endpoint URL stops working immediately — senders will get 404s. The delivery log is removed with it."
              : "Hive will stop delivering events to this endpoint. The delivery log is removed with it."}
          </p>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => {
                void remove(confirmDelete.id);
                setConfirmDelete(null);
              }}
              className="flex-1 rounded-lg bg-rose-500 py-2 text-sm font-semibold text-white transition hover:brightness-105"
            >
              Delete
            </button>
            <button
              onClick={() => setConfirmDelete(null)}
              className="flex-1 rounded-lg border border-app py-2 text-sm font-medium text-app-1 transition hover:border-app-strong"
            >
              Keep it
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
